import { Link } from 'gatsby';
import { ImageDataLike } from 'gatsby-plugin-image';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { createMarkup } from '../utils/createMarkup';
import { getUrlParts } from '../utils/getUrlParts';
import { CaretRight } from './icon/icons/CaretRight';
import { Img } from './Img';
import { Panel } from './Panel';
import { Typography } from './Typography';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import { Tag } from './Tag';

export interface CardProps {
  backgroundColor?: 'white' | 'grey1';
  heading: string;
  tag?: string;
  lead?: string;
  meta?: string;
  url?: string;
  actionLabel?: string;
  onClick?: any;
  image?: ImageDataLike;
  footerMeta?: string;
}

export interface CardWrapperProps {
  url?: string;
  onClick?: any;
  rest?: any;
}

interface StyledBodyProps {
  hasImage: boolean;
}

const StyledAction = styled.div`
  ${({ theme: { space, borders, colors, media } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.sm};
    padding-top: ${space.md};
    border-top: ${borders.default};
    color: ${colors.brand.default};
    margin-top: ${space.lg};

    a[href^='tel:'] {
      color: ${colors.black.default};
    }

    @media (min-width: ${media.md}) {
      padding-bottom: ${space.md};
    }
  `};
`;

const StyledCard = styled.div`
  ${({ theme: { global, shadows } }) => css`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    text-align: left;
    border-radius: ${global.borderRadius.default};
    overflow: hidden;
    height: 100%;

    .gatsby-image-wrapper {
      border-bottom-right-radius: 0px;
      border-bottom-left-radius: 0px;
    }

    &:hover {
      box-shadow: ${shadows.small};

      ${StyledAction} {
        text-decoration: underline;
      }
    }
  `};
`;

const StyledBody = styled(Panel)<StyledBodyProps>`
  ${({ hasImage }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;

    ${hasImage &&
    css`
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    `};
  `};
`;

const StyledHeader = styled.div`
  flex-grow: 1;
`;

const StyledTag = styled(Tag)`
  ${({ theme: { space, colors, global } }) => css`
    width: fit-content;
    margin-bottom: ${space.md};
  `};
`;

const StyledLead = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.sm};
  `};
`;

const StyledMeta = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.md};
  `};
`;

// const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

const CardWrapper: FC<React.PropsWithChildren<CardWrapperProps>> = ({ url, onClick, children, rest }) => {
  if (url) {
    return (
      <StyledCard as={Link} to={url} onClick={onClick} {...rest}>
        {children}
      </StyledCard>
    );
  }
  if (!url && onClick) {
    return (
      <StyledCard as="button" type="button" onClick={onClick} {...rest}>
        {children}
      </StyledCard>
    );
  }
  return (
    <StyledCard as="div" {...rest}>
      {children}
    </StyledCard>
  );
};

export const Card: FC<React.PropsWithChildren<CardProps>> = ({
  backgroundColor = 'grey1',
  heading,
  tag,
  lead,
  meta,
  actionLabel,
  url,
  onClick,
  footerMeta,
  image,
  ...rest
}) => {
  const { dataLayerPush } = useDataLayerPush();

  const handleClick = (...props) => {
    dataLayerPush({
      event: 'navigation card',
      clickText: heading,
      clickBladeId: undefined,
      ...getUrlParts(url),
    });
    if (onClick) {
      onClick(...props);
    }
  };

  return (
    <CardWrapper onClick={handleClick} url={url} rest={rest}>
      {image && <Img src={image} />}
      <StyledBody backgroundColor={backgroundColor} hasImage={!!image}>
        {tag && <StyledTag text={tag} />}
        <StyledHeader>
          {heading && (
            <Typography component="h3" variant="titleMedium">
              {heading}
            </Typography>
          )}
          {lead && (
            <StyledLead component="p" variant="lead">
              {lead}
            </StyledLead>
          )}
          {meta && (
            <StyledMeta component="p" variant="bodyLarge">
              {createMarkup(meta)}
            </StyledMeta>
          )}
        </StyledHeader>

        {actionLabel && (
          <StyledAction>
            {actionLabel}
            <CaretRight />
          </StyledAction>
        )}

        {footerMeta && <StyledAction>{createMarkup(footerMeta)}</StyledAction>}
      </StyledBody>
    </CardWrapper>
  );
};
